import {
  useEffect,
  useMemo,
  useState,
} from "react";
import { gtApi } from "../../api/generalTradeApi";
import { useBusy } from "../../../context/BusyContext";
import { useMessageModal } from "../../contexts/MessageModalContext";
import "../../styles/generalTrade.css";

const today = () =>
  new Date().toISOString().slice(0, 10);

const UOMS = [
  "Unit",
  "Bag",
  "Gunni",
  "Roll",
  "Liters",
  "Packet",
  "Box",
  "Carton",
] as const;

type UomType = (typeof UOMS)[number];

type DiscountType =
  | "none"
  | "percentage"
  | "amount";

type InvoiceStatus =
  | "draft"
  | "submitted";

type FormMode =
  | "add"
  | "edit"
  | "view";

interface InvoiceItem {
  row_id: string;
  product_id: number;
  product_name_snapshot: string;
  sku_snapshot: string;
  qty: number | string;
  uom: UomType;
  selling_price: number | string;
  discount_type: DiscountType;
  discount_value: number | string;
}

interface Props {
  mode: FormMode;
  invoiceId?: number | null;
  deliveryOrder?: any | null;
  onCancel: () => void;
  onSaved: () => void;
}

const createRowId = () =>
  `${Date.now()}-${Math.random()
    .toString(36)
    .slice(2)}`;

const round2 = (value: number) =>
  Number(value.toFixed(2));

const calculateDiscount = (
  baseAmount: number,
  discountType: DiscountType,
  discountValue: number,
) => {
  const base = Math.max(
    Number(baseAmount || 0),
    0,
  );

  const value = Math.max(
    Number(discountValue || 0),
    0,
  );

  if (discountType === "percentage") {
    return Math.min(
      round2(
        base *
          (Math.min(value, 100) / 100),
      ),
      base,
    );
  }

  if (discountType === "amount") {
    return Math.min(
      round2(value),
      base,
    );
  }

  return 0;
};

export default function SalesInvoiceForm({
  mode,
  invoiceId = null,
  deliveryOrder = null,
  onCancel,
  onSaved,
}: Props) {
  const { runBusy } = useBusy();
  const { showModal } =
    useMessageModal();

  const isAddMode = mode === "add";
  const isEditMode = mode === "edit";
  const isViewMode = mode === "view";

  const [documentNumber, setDocumentNumber] =
    useState("");

  const [deliveryOrderNumber, setDeliveryOrderNumber] =
    useState("");

  const [salesOrderNumber, setSalesOrderNumber] =
    useState("");

  const [customerName, setCustomerName] =
    useState("");

  const [form, setForm] = useState({
    delivery_order_id:
      deliveryOrder?.id || "",
    invoice_date: today(),
    due_date: today(),
    discount_type:
      "none" as DiscountType,
    discount_value: 0 as
      | number
      | string,
    status:
      "draft" as InvoiceStatus,
    remarks: "",
    paid_amount: 0,
    outstanding_amount: 0,
    payment_status: "unpaid",
  });

  const [items, setItems] =
    useState<InvoiceItem[]>([]);

  const [loading, setLoading] =
    useState(false);

  const [submitting, setSubmitting] =
    useState(false);

  const disabled =
    isViewMode ||
    loading ||
    submitting;

  useEffect(() => {
    let cancelled = false;

    const loadForm =
      async (): Promise<void> => {
        try {
          setLoading(true);

          let source: any = null;

          if (
            isAddMode &&
            deliveryOrder?.id
          ) {
            source =
              await runBusy(() =>
                gtApi.deliveryOrder(
                  Number(deliveryOrder.id),
                ),
              );
          } else if (
            !isAddMode &&
            invoiceId
          ) {
            source =
              await runBusy(() =>
                gtApi.salesInvoice(
                  Number(invoiceId),
                ),
              );
          }

          if (
            cancelled ||
            !source
          ) {
            return;
          }

          setDocumentNumber(
            source.sales_invoice_number ||
              "",
          );

          setDeliveryOrderNumber(
            source.delivery_order_number ||
              deliveryOrder
                ?.delivery_order_number ||
              "",
          );

          setSalesOrderNumber(
            source.sales_order_number ||
              deliveryOrder
                ?.sales_order_number ||
              "",
          );

          setCustomerName(
            source.company_name ||
              deliveryOrder?.company_name ||
              "",
          );

          setForm({
            delivery_order_id:
              source.delivery_order_id ||
              source.id ||
              deliveryOrder?.id ||
              "",
            invoice_date:
              String(
                source.invoice_date ||
                  today(),
              ).slice(0, 10),
            due_date:
              String(
                source.due_date ||
                  today(),
              ).slice(0, 10),
            discount_type:
              source.discount_type ||
              "none",
            discount_value:
              source.discount_value ||
              0,
            status:
              source.status ===
              "submitted"
                ? "submitted"
                : "draft",
            remarks:
              source.remarks || "",
            paid_amount: Number(
              source.paid_amount || 0,
            ),
            outstanding_amount:
              Number(
                source.outstanding_amount ||
                  0,
              ),
            payment_status:
              source.payment_status ||
              "unpaid",
          });

          const preparedItems =
            (
              Array.isArray(source.items)
                ? source.items
                : []
            ).map(
              (item: any) => ({
                row_id: createRowId(),
                product_id: Number(
                  item.product_id,
                ),
                product_name_snapshot:
                  item.product_name_snapshot,
                sku_snapshot:
                  item.sku_snapshot ||
                  "",
                qty: item.qty,
                uom: UOMS.includes(
                  item.uom,
                )
                  ? item.uom
                  : "Unit",
                selling_price:
                  item.selling_price,
                discount_type:
                  item.discount_type ||
                  "none",
                discount_value:
                  item.discount_value ||
                  0,
              }),
            );

          setItems(preparedItems);
        } catch (error: any) {
          if (!cancelled) {
            showModal({
              type: "error",
              title: "Unable to Load",
              message:
                error?.response?.data
                  ?.message ||
                "Unable to load the Sales Invoice.",
              confirmText: "OK",
            });
          }
        } finally {
          if (!cancelled) {
            setLoading(false);
          }
        }
      };

    void loadForm();

    return () => {
      cancelled = true;
    };
  }, [
    isAddMode,
    invoiceId,
    deliveryOrder,
    runBusy,
    showModal,
  ]);

  const calculatedItems =
    useMemo(
      () =>
        items.map((item) => {
          const qty = Number(
            item.qty || 0,
          );

          const price = Number(
            item.selling_price || 0,
          );

          const gross = round2(
            qty * price,
          );

          const discount =
            calculateDiscount(
              gross,
              item.discount_type,
              Number(
                item.discount_value ||
                  0,
              ),
            );

          return {
            ...item,
            gross_amount: gross,
            discount_amount:
              discount,
            subtotal: round2(
              gross - discount,
            ),
          };
        }),
      [items],
    );

  const subtotal = useMemo(
    () =>
      round2(
        calculatedItems.reduce(
          (sum, item) =>
            sum +
            item.subtotal,
          0,
        ),
      ),
    [calculatedItems],
  );

  const invoiceDiscount =
    useMemo(
      () =>
        calculateDiscount(
          subtotal,
          form.discount_type,
          Number(
            form.discount_value ||
              0,
          ),
        ),
      [
        subtotal,
        form.discount_type,
        form.discount_value,
      ],
    );

  const grandTotal = round2(
    subtotal - invoiceDiscount,
  );

  const calculatedOutstanding =
    round2(
      grandTotal -
        Number(
          form.paid_amount || 0,
        ),
    );

  const updateItem = (
    rowId: string,
    patch: Partial<InvoiceItem>,
  ) => {
    if (disabled) return;

    setItems((previous) =>
      previous.map((item) =>
        item.row_id === rowId
          ? {
              ...item,
              ...patch,
            }
          : item,
      ),
    );
  };

  const submit = async (
    targetStatus: InvoiceStatus,
  ): Promise<void> => {
    if (
      isViewMode ||
      submitting
    ) {
      return;
    }

    if (!items.length) {
      showModal({
        type: "error",
        title: "Products Required",
        message:
          "The Sales Invoice must contain at least one product.",
        confirmText: "OK",
      });

      return;
    }

    const paidAmount = Number(
      form.paid_amount || 0,
    );

    if (grandTotal < paidAmount) {
      showModal({
        type: "error",
        title:
          "Invoice Total Too Low",
        message: `Grand Total cannot be less than the amount already paid (RM ${paidAmount.toFixed(2)}).`,
        confirmText: "OK",
      });

      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        delivery_order_id: Number(
          form.delivery_order_id,
        ),
        invoice_date:
          form.invoice_date,
        due_date: form.due_date,

        discount_type:
          form.discount_type,
        discount_value: Number(
          form.discount_value ||
            0,
        ),

        status: targetStatus,
        remarks:
          form.remarks.trim(),

        items:
          calculatedItems.map(
            (item) => ({
              product_id:
                item.product_id,
              product_name_snapshot:
                item.product_name_snapshot,
              sku_snapshot:
                item.sku_snapshot ||
                null,
              qty: Number(
                item.qty,
              ),
              uom: item.uom,
              selling_price:
                Number(
                  item.selling_price,
                ),
              discount_type:
                item.discount_type,
              discount_value:
                Number(
                  item.discount_value ||
                    0,
                ),
            }),
          ),
      };

      const response =
        await runBusy(() => {
          if (
            isEditMode &&
            invoiceId
          ) {
            return gtApi.updateSalesInvoice(
              Number(invoiceId),
              payload,
            );
          }

          return gtApi.createSalesInvoice(
            payload,
          );
        });

      showModal({
        type: "success",
        title: isEditMode
          ? "Sales Invoice Updated"
          : "Sales Invoice Created",
        message:
          response?.message ||
          (isEditMode
            ? "Sales Invoice updated successfully."
            : "Sales Invoice created successfully."),
        confirmText: "OK",
      });

      onSaved();
    } catch (error: any) {
      showModal({
        type: "error",
        title: "Unable to Save",
        message:
          error?.response?.data
            ?.message ||
          "Unable to save the Sales Invoice.",
        confirmText: "OK",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="gt-card">
      <div className="gt-document-title-row">
        <div>
          <h2>
            {isAddMode
              ? "NEW SALES INVOICE"
              : isEditMode
                ? "EDIT SALES INVOICE"
                : "VIEW SALES INVOICE"}
          </h2>

          {documentNumber && (
            <span className="gt-document-number">
              {documentNumber}
            </span>
          )}
        </div>
      </div>

      <div className="gt-invoice-reference-grid">
        <label>
          Delivery Order
          <input
            disabled
            value={
              deliveryOrderNumber
            }
          />
        </label>

        <label>
          Sales Order
          <input
            disabled
            value={salesOrderNumber}
          />
        </label>

        <label>
          Customer
          <input
            disabled
            value={customerName}
          />
        </label>

        <label>
          Invoice Date
          <input
            type="date"
            disabled={disabled}
            value={
              form.invoice_date
            }
            onChange={(event) =>
              setForm(
                (previous) => ({
                  ...previous,
                  invoice_date:
                    event.target.value,
                }),
              )
            }
          />
        </label>

        <label>
          Due Date
          <input
            type="date"
            disabled={disabled}
            value={form.due_date}
            onChange={(event) =>
              setForm(
                (previous) => ({
                  ...previous,
                  due_date:
                    event.target.value,
                }),
              )
            }
          />
        </label>
      </div>

      <div className="gt-invoice-lines">
        {calculatedItems.map(
          (item, index) => (
            <div
              className="gt-invoice-line"
              key={item.row_id}
            >
              <div className="gt-line-head">
                <strong>
                  Product #{index + 1}
                </strong>
              </div>

              <div className="gt-invoice-line-grid">
                <label>
                  Product
                  <input
                    disabled
                    value={
                      item.product_name_snapshot
                    }
                  />
                </label>

                <label>
                  SKU
                  <input
                    disabled
                    value={
                      item.sku_snapshot
                    }
                  />
                </label>

                <label>
                  Qty
                  <input
                    type="number"
                    min="0.001"
                    step="0.001"
                    disabled={disabled}
                    value={item.qty}
                    onChange={(event) =>
                      updateItem(
                        item.row_id,
                        {
                          qty:
                            event.target
                              .value,
                        },
                      )
                    }
                  />
                </label>

                <label>
                  UOM
                  <select
                    disabled={disabled}
                    value={item.uom}
                    onChange={(event) =>
                      updateItem(
                        item.row_id,
                        {
                          uom:
                            event.target
                              .value as UomType,
                        },
                      )
                    }
                  >
                    {UOMS.map(
                      (uom) => (
                        <option
                          key={uom}
                          value={uom}
                        >
                          {uom}
                        </option>
                      ),
                    )}
                  </select>
                </label>

                <label>
                  Selling Price
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    disabled={disabled}
                    value={
                      item.selling_price
                    }
                    onChange={(event) =>
                      updateItem(
                        item.row_id,
                        {
                          selling_price:
                            event.target
                              .value,
                        },
                      )
                    }
                  />
                </label>

                <label>
                  Gross Amount
                  <input
                    disabled
                    value={
                      item.gross_amount.toFixed(
                        2,
                      )
                    }
                  />
                </label>

                <label>
                  Discount Type
                  <select
                    disabled={disabled}
                    value={
                      item.discount_type
                    }
                    onChange={(event) =>
                      updateItem(
                        item.row_id,
                        {
                          discount_type:
                            event.target
                              .value as DiscountType,
                          discount_value:
                            event.target
                              .value ===
                            "none"
                              ? 0
                              : item.discount_value,
                        },
                      )
                    }
                  >
                    <option value="none">
                      None
                    </option>

                    <option value="percentage">
                      Percentage
                    </option>

                    <option value="amount">
                      Amount
                    </option>
                  </select>
                </label>

                <label>
                  Discount Value
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    disabled={
                      disabled ||
                      item.discount_type ===
                        "none"
                    }
                    value={
                      item.discount_value
                    }
                    onChange={(event) =>
                      updateItem(
                        item.row_id,
                        {
                          discount_value:
                            event.target
                              .value,
                        },
                      )
                    }
                  />
                </label>

                <label>
                  Discount Amount
                  <input
                    disabled
                    value={
                      item.discount_amount.toFixed(
                        2,
                      )
                    }
                  />
                </label>

                <label>
                  Line Total
                  <input
                    disabled
                    value={item.subtotal.toFixed(
                      2,
                    )}
                  />
                </label>
              </div>
            </div>
          ),
        )}
      </div>

      <label>
        Remarks
        <textarea
          disabled={disabled}
          value={form.remarks}
          onChange={(event) =>
            setForm(
              (previous) => ({
                ...previous,
                remarks:
                  event.target.value,
              }),
            )
          }
        />
      </label>

      <div className="gt-invoice-summary">
        <div className="gt-invoice-discount-fields">
          <label>
            Overall Discount Type
            <select
              disabled={disabled}
              value={
                form.discount_type
              }
              onChange={(event) =>
                setForm(
                  (previous) => ({
                    ...previous,
                    discount_type:
                      event.target
                        .value as DiscountType,
                    discount_value:
                      event.target.value ===
                      "none"
                        ? 0
                        : previous.discount_value,
                  }),
                )
              }
            >
              <option value="none">
                None
              </option>

              <option value="percentage">
                Percentage
              </option>

              <option value="amount">
                Amount
              </option>
            </select>
          </label>

          <label>
            Overall Discount Value
            <input
              type="number"
              min="0"
              step="0.01"
              disabled={
                disabled ||
                form.discount_type ===
                  "none"
              }
              value={
                form.discount_value
              }
              onChange={(event) =>
                setForm(
                  (previous) => ({
                    ...previous,
                    discount_value:
                      event.target.value,
                  }),
                )
              }
            />
          </label>
        </div>

        <div className="gt-invoice-total-box">
          <div>
            <span>Subtotal</span>
            <strong>
              RM {subtotal.toFixed(2)}
            </strong>
          </div>

          <div>
            <span>
              Overall Discount
            </span>
            <strong>
              RM{" "}
              {invoiceDiscount.toFixed(
                2,
              )}
            </strong>
          </div>

          <div className="grand-total">
            <span>Grand Total</span>
            <strong>
              RM{" "}
              {grandTotal.toFixed(2)}
            </strong>
          </div>

          <div>
            <span>Paid Amount</span>
            <strong>
              RM{" "}
              {Number(
                form.paid_amount,
              ).toFixed(2)}
            </strong>
          </div>

          <div>
            <span>
              Outstanding
            </span>
            <strong>
              RM{" "}
              {Math.max(
                calculatedOutstanding,
                0,
              ).toFixed(2)}
            </strong>
          </div>
        </div>
      </div>

      <div className="gt-footer">
        <div>
          <button
            type="button"
            className="btn-secondary"
            onClick={onCancel}
            disabled={submitting}
          >
            {isViewMode
              ? "Back"
              : "Cancel"}
          </button>

          {!isViewMode && (
            <>
              <button
                type="button"
                className="btn-secondary"
                disabled={
                  submitting ||
                  loading
                }
                onClick={() =>
                  void submit("draft")
                }
              >
                Save Draft
              </button>

              <button
                type="button"
                className="btn-primary"
                disabled={
                  submitting ||
                  loading
                }
                onClick={() =>
                  void submit(
                    "submitted",
                  )
                }
              >
                {isEditMode
                  ? "Update Invoice"
                  : "Submit Invoice"}
              </button>
            </>
          )}
        </div>

        <strong>
          Grand Total: RM{" "}
          {grandTotal.toFixed(2)}
        </strong>
      </div>
    </div>
  );
}